/**
 * KPI Thresholds tab.
 *
 * Lets the user define Red / Amber / Green thresholds for any KPI from the
 * calculation result, with user-pickable comparison operators (<, ≤, >, ≥).
 * Thresholds are persisted in localStorage and re-evaluated on every render.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gauge, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { makeFmt } from './currency';

const STORAGE_KEY = 'construction_kpi_thresholds_v1';
const OPS = [
  { v: '<',  label: 'less than (<)' },
  { v: '<=', label: 'less than or equal (≤)' },
  { v: '>',  label: 'greater than (>)' },
  { v: '>=', label: 'greater than or equal (≥)' },
];

// Available KPIs (resolved from calculation + risk + funding-derived numbers).
// `fmt` receives the calculation-aware fmt$ as second arg so currency-bearing
// KPIs render in the active region's local currency.
const KPI_LIBRARY = [
  { key: 'final_cost_usd',           label: 'Final cost',                       path: ['final_cost_usd'],                              fmt: (v, fmt$) => fmt$(v) },
  { key: 'total_direct_cost_usd',    label: 'Direct cost',                      path: ['total_direct_cost_usd'],                       fmt: (v, fmt$) => fmt$(v) },
  { key: 'total_labour_hours',       label: 'Total labour hours',               path: ['total_labour_hours'],                          fmt: v => `${Number(v||0).toLocaleString(undefined,{maximumFractionDigits:0})} hrs` },
  { key: 'indicative_avg_workforce', label: 'Average workforce (pax)',          path: ['indicative_avg_workforce'],                    fmt: v => `${v} pax` },
  { key: 'inflation_uplift_pct',     label: 'Inflation uplift (%)',             path: ['inflation_uplift_pct'],                        fmt: v => `${Number(v||0).toFixed(2)}%` },
  { key: 'effective_cost_multiplier',label: 'Effective region/city multiplier', path: ['effective_cost_multiplier'],                   fmt: v => `×${v}` },
  { key: 'expected_risk_cost_usd',   label: 'Expected risk cost',               path: ['__risk__', 'expected_risk_cost_usd'],          fmt: (v, fmt$) => fmt$(v) },
  { key: 'high_risk_count',          label: 'High-priority risks (#)',          path: ['__risk__', 'high_risk_count'],                 fmt: v => `${v}` },
];

const DEFAULT_THRESHOLDS = [
  { id: 1, kpi: 'final_cost_usd',    green_op: '<',  green: 1_000_000, amber_op: '<', amber: 1_500_000 },
  { id: 2, kpi: 'high_risk_count',   green_op: '<=', green: 2,         amber_op: '<=', amber: 5 },
  { id: 3, kpi: 'inflation_uplift_pct', green_op: '<=', green: 5,      amber_op: '<=', amber: 10 },
];

const evaluate = (val, op, threshold) => {
  const v = Number(val); const t = Number(threshold);
  if (Number.isNaN(v) || Number.isNaN(t)) return false;
  switch (op) {
    case '<':  return v <  t;
    case '<=': return v <= t;
    case '>':  return v >  t;
    case '>=': return v >= t;
    default:   return false;
  }
};

const resolvePath = (calc, riskEval, path) => {
  if (!calc) return undefined;
  if (path[0] === '__risk__') return riskEval ? riskEval[path[1]] : undefined;
  return path.reduce((o, k) => (o ? o[k] : undefined), calc);
};

export default function KpiDashboard({ calculation, riskEval }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [rules, setRules] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_THRESHOLDS;
    } catch { return DEFAULT_THRESHOLDS; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rules)); } catch (_) { /* noop */ }
  }, [rules]);

  const addRule = () => setRules(r => [...r, {
    id: Date.now(), kpi: 'final_cost_usd', green_op: '<', green: 0, amber_op: '<', amber: 0,
  }]);
  const updateRule = (id, field, value) => setRules(r => r.map(x => x.id === id ? { ...x, [field]: value } : x));
  const removeRule = (id) => setRules(r => r.filter(x => x.id !== id));
  const resetRules = () => setRules(DEFAULT_THRESHOLDS);

  const evaluated = useMemo(() => rules.map(rule => {
    const def = KPI_LIBRARY.find(k => k.key === rule.kpi);
    if (!def) return { ...rule, status: 'unknown', value: null, def: null };
    const value = resolvePath(calculation, riskEval, def.path);
    let status = 'red';
    if (value != null && !Number.isNaN(Number(value))) {
      if (evaluate(value, rule.green_op, rule.green)) status = 'green';
      else if (evaluate(value, rule.amber_op, rule.amber)) status = 'amber';
    } else status = 'unknown';
    return { ...rule, status, value, def };
  }), [rules, calculation, riskEval]);

  const counts = evaluated.reduce((acc, e) => { acc[e.status] = (acc[e.status]||0)+1; return acc; }, {});

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to evaluate KPIs.</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="kpi-dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat color="bg-emerald-600" label="Green" value={counts.green || 0} />
        <Stat color="bg-amber-500"   label="Amber" value={counts.amber || 0} />
        <Stat color="bg-red-600"     label="Red"   value={counts.red   || 0} />
        <Stat color="bg-slate-400"   label="Unknown / N/A" value={counts.unknown || 0} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base inline-flex items-center gap-1.5"><Gauge className="h-4 w-4" />Custom KPI thresholds</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={resetRules} data-testid="kpi-reset-btn">Reset to defaults</Button>
              <Button size="sm" onClick={addRule} data-testid="kpi-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add KPI</Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Green = condition you want; Amber = warning band; Red = anything else. Use any of {`<, ≤, >, ≥`} to define each band.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">KPI</th>
                  <th className="text-left p-2">Current</th>
                  <th className="text-left p-2">Green if</th>
                  <th className="text-left p-2">Amber if</th>
                  <th className="text-center p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {evaluated.map(rule => (
                  <tr key={rule.id} className="border-t" data-testid={`kpi-row-${rule.kpi}`}>
                    <td className="p-1 min-w-[180px]">
                      <Select value={rule.kpi} onValueChange={v => updateRule(rule.id, 'kpi', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {KPI_LIBRARY.map(k => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-slate-700">
                      {rule.value != null && rule.def ? rule.def.fmt(rule.value, fmt$) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-1 w-56">
                      <div className="flex gap-1">
                        <Select value={rule.green_op} onValueChange={v => updateRule(rule.id, 'green_op', v)}>
                          <SelectTrigger className="h-8 text-sm w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{OPS.map(o => <SelectItem key={o.v} value={o.v}>{o.v}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" className="h-8 text-sm" value={rule.green} onChange={e => updateRule(rule.id, 'green', Number(e.target.value))} />
                      </div>
                    </td>
                    <td className="p-1 w-56">
                      <div className="flex gap-1">
                        <Select value={rule.amber_op} onValueChange={v => updateRule(rule.id, 'amber_op', v)}>
                          <SelectTrigger className="h-8 text-sm w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{OPS.map(o => <SelectItem key={o.v} value={o.v}>{o.v}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" className="h-8 text-sm" value={rule.amber} onChange={e => updateRule(rule.id, 'amber', Number(e.target.value))} />
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <StatusBadge status={rule.status} />
                    </td>
                    <td className="p-1">
                      <Button size="sm" variant="ghost" onClick={() => removeRule(rule.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">No KPIs defined. Add one to start governing your project.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base inline-flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" />Open the Risk &amp; Change tab to see how risks impact KPIs</CardTitle>
        </CardHeader>
      </Card>
    </div>
  );
}

function Stat({ color, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`w-3 h-12 rounded ${color}`} />
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-bold text-2xl text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === 'green') return <Badge className="bg-emerald-600 text-white">GREEN</Badge>;
  if (status === 'amber') return <Badge className="bg-amber-500 text-white">AMBER</Badge>;
  if (status === 'red')   return <Badge className="bg-red-600 text-white">RED</Badge>;
  return <Badge variant="outline">N/A</Badge>;
}
